import { useState } from "react";

import { Box, Text, type TextProps, useToken } from "@hightouch/htui";

/** One sweep takes this many `dramatic` durations: at one it reads as a flicker. */
const SWEEP_DRAMATICS = 5;
const SWEEP = `calc(var(--chakra-transition-duration-dramatic) * ${SWEEP_DRAMATICS}) var(--chakra-transition-easing-easeInOutQuad) infinite`;

/** A soft band of light that sweeps across a tile while an agent works on it: brighter over a bare shape, faint over content. */
export function WorkingSheen({ isFaint = false }: { isFaint?: boolean }) {
  const delay = useSweepDelay();
  return (
    <Box
      aria-hidden
      position="absolute"
      inset={0}
      overflow="hidden"
      pointerEvents="none"
      sx={{
        "@keyframes working-sheen": {
          from: { transform: "translateX(-70%)" },
          to: { transform: "translateX(70%)" },
        },
      }}
    >
      <Box
        position="absolute"
        inset={0}
        bgGradient={`linear(105deg, transparent 30%, ${isFaint ? "whiteAlpha.20" : "whiteAlpha.80"} 50%, transparent 70%)`}
        // Moved by transform alone: a paint animation would repaint the whole canvas every frame.
        animation={`working-sheen ${SWEEP}`}
        // Inline: every tile's delay differs, and as a style prop each would mint a class.
        style={{ animationDelay: delay }}
        _mediaReduceMotion={{ animation: "none", opacity: 0 }}
      />
    </Box>
  );
}

/**
 * The same band along a line of text, in step with the tile's sheen: a lighter copy of the text, laid over it and shown
 * through a soft window. Goes in a `position="relative"` box around the text, at its size.
 */
export function WorkingTextSheen({
  children,
  size,
  color,
}: Pick<TextProps, "children" | "size" | "color">) {
  const delay = useSweepDelay();
  return (
    <Box
      aria-hidden
      position="absolute"
      inset={0}
      overflow="hidden"
      pointerEvents="none"
      // The window runs along the text while the copy in it runs back as far, so the copy stays on the text and both move by transform alone.
      animation={`working-text-window ${SWEEP}`}
      style={{ animationDelay: delay }}
      sx={{
        "@keyframes working-text-window": {
          from: { transform: "translateX(-100%)" },
          to: { transform: "translateX(100%)" },
        },
        "@keyframes working-text-copy": {
          from: { transform: "translateX(100%)" },
          to: { transform: "translateX(-100%)" },
        },
        maskImage:
          "linear-gradient(90deg, transparent 35%, black 50%, transparent 65%)",
      }}
      _mediaReduceMotion={{ animation: "none", opacity: 0 }}
    >
      <Box
        animation={`working-text-copy ${SWEEP}`}
        style={{ animationDelay: delay }}
        _mediaReduceMotion={{ animation: "none" }}
      >
        <Text size={size} color={color} isTruncated display="block">
          {children}
        </Text>
      </Box>
    </Box>
  );
}

/** Set back by the page clock at mount, so every sweep on the canvas moves in step, however late it mounted. */
function useSweepDelay() {
  const dramatic = useToken("transition.duration", "dramatic");
  const [delay] = useState(
    () => -(performance.now() % (parseFloat(dramatic) * SWEEP_DRAMATICS)),
  );
  return `${delay}ms`;
}
